import Phaser from 'phaser';
import { PALETTE } from '../config';

const TAU = Math.PI * 2;

/**
 * On-screen rhythm guide for the alternate-tap run-up. Two dashed/dotted rings (left &
 * right) that pulse in alternation to teach the L-R rhythm and flash when tapped.
 * - Mobile: rings mark exactly where to tap, with a finger hint.
 * - Desktop: rings are labelled with the keys (A / ← and D / →).
 * Fixed to the camera; call update(dt) each frame and flash(side) on each tap.
 */
export class TapZones {
  private readonly left: Phaser.GameObjects.Container;
  private readonly right: Phaser.GameObjects.Container;
  private phase = 0;
  private leftKick = 0;
  private rightKick = 0;

  constructor(scene: Phaser.Scene) {
    const w = scene.scale.width;
    const h = scene.scale.height;
    const desktop = scene.sys.game.device.os.desktop;
    const r = Math.min(56, w * 0.07);
    const y = h - Math.max(120, h * 0.2);
    const labelL = desktop ? 'A  ◀' : 'TAP';
    const labelR = desktop ? '▶  D' : 'TAP';
    this.left = this.makeRing(scene, w * 0.16, y, r, PALETTE.laneAccent, labelL, desktop);
    this.right = this.makeRing(scene, w * 0.84, y, r, PALETTE.cadence, labelR, desktop);

    const hint = scene.add.text(w / 2, y, desktop ? 'Alterna i tasti A e D a ritmo!' : 'Tocca SX e DX a ritmo, alternando!', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(Math.min(20, w * 0.022))}px`, color: '#d3dbf2',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(30).setAlpha(0.85);
    scene.tweens.add({ targets: hint, alpha: 0, delay: 3500, duration: 800 });
  }

  private makeRing(scene: Phaser.Scene, x: number, y: number, r: number, color: number, label: string, desktop: boolean): Phaser.GameObjects.Container {
    const g = scene.add.graphics();
    const dots = 16;
    for (let i = 0; i < dots; i++) {
      const a = (i / dots) * TAU;
      g.fillStyle(color, 1).fillCircle(Math.cos(a) * r, Math.sin(a) * r, Math.max(3, r * 0.07));
    }
    g.fillStyle(color, 0.12).fillCircle(0, 0, r);
    const txt = scene.add.text(0, 0, desktop ? label : '👆', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(desktop ? r * 0.5 : r * 0.9)}px`, color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);
    return scene.add.container(x, y, [g, txt]).setScrollFactor(0).setDepth(30);
  }

  /** Quick feedback flash on the tapped side. */
  flash(side: 'L' | 'R'): void {
    if (side === 'L') this.leftKick = 1;
    else this.rightKick = 1;
  }

  update(dt: number): void {
    this.phase += dt * 5; // gentle alternation pace
    this.leftKick = Math.max(0, this.leftKick - dt * 4);
    this.rightKick = Math.max(0, this.rightKick - dt * 4);
    // Alternate brightness/scale: left on the first half of the cycle, right on the second.
    const lBeat = 0.5 + 0.5 * Math.sin(this.phase);
    const rBeat = 0.5 + 0.5 * Math.sin(this.phase + Math.PI);
    this.left.setScale(1 + lBeat * 0.08 + this.leftKick * 0.3);
    this.left.setAlpha(0.5 + lBeat * 0.4 + this.leftKick * 0.1);
    this.right.setScale(1 + rBeat * 0.08 + this.rightKick * 0.3);
    this.right.setAlpha(0.5 + rBeat * 0.4 + this.rightKick * 0.1);
  }

  setVisible(v: boolean): void {
    this.left.setVisible(v);
    this.right.setVisible(v);
  }

  destroy(): void {
    this.left.destroy();
    this.right.destroy();
  }
}
